import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from './types';

/* ------------------------------------------------------------------ *
 *  Tek HTTP istemcisi.
 *
 *  Sozlesme: her uc `Response<T>` zarfi doner. Basarili yanitta `data`,
 *  hatali yanitta `error.errors` doludur. Bu dosya zarfi acar; cagiran
 *  taraf ya veriyi alir ya da ApiError yakalar.
 * ------------------------------------------------------------------ */

/** Sunucudan donen is kurali veya dogrulama hatasi. */
export class ApiError extends Error {
  readonly status: number;
  readonly errors: string[];
  readonly code: string | null;
  readonly traceId: string | null;
  /** Hata kullaniciya gosterilmeli mi (backend `isShow` bayragi). */
  readonly isShow: boolean;

  constructor(
    status: number,
    errors: string[],
    code: string | null = null,
    traceId: string | null = null,
    isShow = true,
  ) {
    super(errors[0] ?? 'Beklenmeyen bir hata oluştu.');
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.code = code;
    this.traceId = traceId;
    this.isShow = isShow;
  }

  /** Oturumun dustugu, girise yonlendirilmesi gereken durum. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Es zamanli degisiklik veya is kurali cakismasi (409). */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Hesap kilitli (423) — giris ekraninda ayri gosterilir. */
  get isLocked(): boolean {
    return this.status === 423;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

const ACCESS_TOKEN_KEY = 'ecommerce.admin.accessToken';
const SESSION_FLAG_KEY = 'ecommerce.admin.hasSession';

let accessToken: string | null = readStoredToken();
const tokenListeners = new Set<(token: string | null) => void>();

function readStoredToken(): string | null {
  try {
    return window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Access token yalnizca bellekte ve sessionStorage'da tutulur.
 * Refresh token'a hic dokunulmaz: o HttpOnly cerezde durur ve
 * JavaScript tarafindan okunamaz.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token) {
      window.sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
      window.localStorage.setItem(SESSION_FLAG_KEY, '1');
    } else {
      window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      window.localStorage.removeItem(SESSION_FLAG_KEY);
    }
  } catch {
    /* Depolama kapaliysa bellekteki deger yeterlidir. */
  }
  tokenListeners.forEach((listener) => listener(token));
}

/**
 * Daha once oturum acilmis mi. Refresh token HttpOnly cerezde oldugu icin
 * JavaScript varligini goremez; bu bayrak sayesinde misafir ziyaretcide
 * her sayfa aciliminda bosuna yenileme istegi atilmaz.
 */
export function hadSession(): boolean {
  try {
    return window.localStorage.getItem(SESSION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onTokenChange(listener: (token: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

/* --------------------- Sorgu dizesi olusturucu --------------------- */

/**
 * ASP.NET Core model baglayicisinin bekledigi bicimde sorgu dizesi uretir.
 * Axios'un varsayilani `brandIds[]=1` yazar; ASP.NET bunu baglayamaz, bu
 * yuzden diziler ayni anahtarin tekrariyla, sozlukler `anahtar[alt]`
 * bicimiyle yazilir.
 */
export function serializeParams(params: unknown): string {
  const parts: string[] = [];

  const push = (key: string, value: unknown): void => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item !== null && typeof item === 'object') {
          push(`${key}[${index}]`, item);
        } else if (item !== null && item !== undefined && item !== '') {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
        }
      });
      return;
    }

    if (value instanceof Date) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.toISOString())}`);
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
        push(`${key}[${childKey}]`, childValue);
      });
      return;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  if (params && typeof params === 'object') {
    Object.entries(params as Record<string, unknown>).forEach(([key, value]) => push(key, value));
  }

  return parts.join('&');
}

export const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  // Refresh ve misafir sepeti cerezleri icin zorunlu.
  withCredentials: true,
  timeout: 60_000,
  headers: { Accept: 'application/json' },
  paramsSerializer: { serialize: serializeParams },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

/* ------------------------- Token yenileme ------------------------- */

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/** Ayni anda birden fazla 401 gelirse tek yenileme yapilir. */
let refreshPromise: Promise<string | null> | null = null;

/** Yenileme de basarisiz olunca uygulamaya haber verilir. */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

/** Yenileme denenmeyecek uclar: bunlarda 401 zaten beklenen sonuctur. */
const NO_REFRESH_PATHS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/register',
  '/api/auth/verify-two-factor',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const { data } = await axios.post<
        ApiResponse<{ accessToken?: string; token?: { accessToken?: string } }>
      >(
        '/api/auth/refresh',
        {},
        {
          baseURL: import.meta.env.VITE_API_BASE_URL || '',
          withCredentials: true,
          headers: { Accept: 'application/json' },
        },
      );
      const token = data.data?.token?.accessToken ?? data.data?.accessToken ?? null;
      setAccessToken(token);
      return token;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      // Sonraki 401 yeni bir yenileme baslatabilsin.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status ?? 0;
    const url = config?.url ?? '';

    const refreshable =
      status === 401 &&
      config !== undefined &&
      !config._retried &&
      !NO_REFRESH_PATHS.some((path) => url.startsWith(path));

    if (refreshable) {
      config._retried = true;
      const token = await refreshAccessToken();

      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
        return http.request(config);
      }

      onSessionExpired?.();
    }

    return Promise.reject(toApiError(error));
  },
);

function toApiError(error: AxiosError<ApiResponse<unknown>>): ApiError {
  if (error.response) {
    const body = error.response.data;
    const errors = body?.error?.errors ?? [];

    return new ApiError(
      error.response.status,
      errors.length > 0 ? errors : [defaultMessageFor(error.response.status)],
      body?.error?.code ?? null,
      body?.traceId ?? null,
      body?.error?.isShow ?? true,
    );
  }

  if (error.code === 'ECONNABORTED') {
    return new ApiError(0, ['İstek zaman aşımına uğradı. Lütfen tekrar deneyiniz.']);
  }

  return new ApiError(0, ['Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol ediniz.']);
}

function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'Gönderilen bilgiler geçersiz.';
    case 401:
      return 'Bu işlem için giriş yapmalısınız.';
    case 403:
      return 'Bu işlem için yetkiniz yok.';
    case 404:
      return 'Kayıt bulunamadı.';
    case 409:
      return 'İşlem mevcut durumla çakışıyor.';
    case 423:
      return 'Hesabınız geçici olarak kilitlendi.';
    case 429:
      return 'Çok fazla istek gönderdiniz. Lütfen biraz bekleyiniz.';
    default:
      return status >= 500
        ? 'Sunucuda beklenmeyen bir hata oluştu.'
        : 'İşlem tamamlanamadı.';
  }
}

/* ----------------------- Zarf acan yardimcilar ---------------------- */

/** Zarfi acar; `data` bos donerse hata firlatir. */
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<ApiResponse<T>>(config);
  const body = response.data;

  if (body?.data === null || body?.data === undefined) {
    throw new ApiError(
      response.status,
      body?.error?.errors ?? ['Sunucu beklenen veriyi döndürmedi.'],
      body?.error?.code ?? null,
      body?.traceId ?? null,
    );
  }

  return body.data;
}

/** Veri donmeyen uclar icin: basari mesajini dondurur. */
export async function command(config: AxiosRequestConfig): Promise<string | null> {
  const response = await http.request<ApiResponse<unknown>>(config);
  return response.data?.messages?.successData?.[0] ?? null;
}

/** Zarfin tamamina ihtiyac duyulan (ornegin `data` bos olabilen) uclar. */
export async function envelope<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const response = await http.request<ApiResponse<T>>(config);
  return response.data;
}

export const api = {
  get: <T>(url: string, params?: unknown) => request<T>({ method: 'GET', url, params }),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    request<T>({ ...config, method: 'POST', url, data }),
  put: <T>(url: string, data?: unknown) => request<T>({ method: 'POST', url, data }),
  patch: <T>(url: string, data?: unknown) => request<T>({ method: 'POST', url, data }),
  delete: <T>(url: string, params?: unknown) => request<T>({ method: 'POST', url, params, data: params }),

  /** Veri donmeyen komut uclari. */
  postCommand: (url: string, data?: unknown, config?: AxiosRequestConfig) =>
    command({ ...config, method: 'POST', url, data }),
  putCommand: (url: string, data?: unknown) => command({ method: 'POST', url, data }),
  patchCommand: (url: string, data?: unknown) => command({ method: 'POST', url, data }),
  deleteCommand: (url: string, params?: unknown) => command({ method: 'POST', url, params, data: params }),
};
